/*
  Mobile "infinite loop" peek carousel for grid-layout PDP galleries
  (general and Celine alike). Deliberately NOT built on top of
  <slider-component> (global.js)
  — that class is shared by ~10 other sliders site-wide (related products,
  collection lists, blog, etc.) and snapshots its slide list once at
  construction time, so its own scrollLeft-based page math has no idea
  about slides added afterward. Rather than touch that shared component to
  make it clone-aware (risking every other slider on the site), this runs
  as a fully separate, additive script scoped only to galleries the Liquid
  opts in via data-loop-gallery, using its own scroll tracking throughout.

  Mechanism: clone the last real slide and prepend it, clone the first
  real slide and append it, then silently (no animation) start scrolled to
  the real first slide. Whenever a scroll settles on a clone, silently
  jump to the real slide it stands in for — the clone is what's visible
  mid-swipe (so the peek shows a real image, not empty space), but the
  slider is never actually resting on it, which is what makes the loop
  feel seamless instead of visibly snapping back.
*/
(() => {
  function initLoopGallery(sliderComponent) {
    const list = sliderComponent.querySelector('[id^="Slider-"]');
    if (!list) return;

    const realSlides = Array.from(list.children).filter((el) => el.matches('.product__media-item'));
    if (realSlides.length < 2) return;

    const firstClone = realSlides[0].cloneNode(true);
    const lastClone = realSlides[realSlides.length - 1].cloneNode(true);

    [firstClone, lastClone].forEach((clone) => {
      clone.removeAttribute('id');
      clone.removeAttribute('data-media-id');
      clone.setAttribute('data-clone', 'true');
      clone.setAttribute('inert', '');
      clone.classList.remove('is-active');
    });

    list.insertBefore(lastClone, realSlides[0]);
    list.appendChild(firstClone);

    const realFor = new Map([
      [lastClone, realSlides[realSlides.length - 1]],
      [firstClone, realSlides[0]],
    ]);

    realSlides[0].scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'instant' });

    const closestToCenter = () => {
      const containerCenter = list.getBoundingClientRect().left + list.clientWidth / 2;
      let closest = null;
      let closestDistance = Infinity;
      Array.from(list.children).forEach((child) => {
        const rect = child.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - containerCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closest = child;
        }
      });
      return closest;
    };

    let settleTimer;
    list.addEventListener('scroll', () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        const current = closestToCenter();
        const real = realFor.get(current);
        if (real) real.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'instant' });
      }, 120);
    });
  }

  function init() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    document.querySelectorAll('slider-component[data-loop-gallery="true"]').forEach(initLoopGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
